import { useState, useEffect } from "react";
import PolicyViewer from "./PolicyViewer";
import { generateDraftPDF, generateFinalPDF } from "../utils/pdfUtils";
import { supabase } from "../utils/supabaseClient";

function PolicyTab({ role, username }) {
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [filter, setFilter] = useState(role === "L2" ? "forwarded" : "all");
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState("");
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [currentPolicyId, setCurrentPolicyId] = useState(null);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("policy_pdfs")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching policies:", error);
    } else {
      setPolicies(data || []);
    }
    setLoading(false);
  };

  const handleGenerateDraftPDF = async (policyId) => {
    try {
      const { data: policy, error: fetchError } = await supabase
        .from("policy_pdfs")
        .select("*")
        .eq("id", policyId)
        .single();

      if (fetchError || !policy) {
        console.error("Error fetching policy:", fetchError);
        alert("Policy not found");
        return;
      }

      const blob = generateDraftPDF(policy, {
        name: username,
        designation: "L1 Admin",
      });

      const fileName = `draft_${policyId}_${Date.now()}.pdf`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("policies")
        .upload(fileName, blob, {
          contentType: "application/pdf",
          upsert: true,
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from("policies")
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from("policy_pdfs")
        .update({
          status: "draft",
          draft_url: urlData.publicUrl,
          prepared_by_name: username,
          prepared_by_designation: "L1 Admin",
          prepared_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", policyId);

      if (updateError) throw updateError;

      await logAudit(policyId, "draft_generated", "pending", "draft", "");
      await fetchPolicies();
      alert("Draft PDF generated successfully!");
    } catch (error) {
      console.error("Error generating draft:", error);
      alert("Failed to generate draft PDF: " + error.message);
    }
  };

  const forwardToL2 = async (id) => {
    try {
      const { error } = await supabase
        .from("policy_pdfs")
        .update({
          status: "forwarded",
          forwarded_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;

      await logAudit(id, "forwarded", "draft", "forwarded", "");
      fetchPolicies();
      alert("Policy forwarded to L2 Compliance Officer!");
    } catch (error) {
      console.error("Error forwarding policy:", error);
      alert("Failed to forward policy");
    }
  };

  const openRemarksModal = (action, policyId) => {
    setCurrentAction(action);
    setCurrentPolicyId(policyId);
    setRemarks("");
    setShowRemarksModal(true);
  };

  const handleSubmitRemarks = async () => {
    if (!remarks.trim()) {
      alert("Please enter remarks");
      return;
    }

    try {
      let updateData = { updated_at: new Date().toISOString() };

      if (currentAction === "approve") {
        updateData = {
          ...updateData,
          status: "approved",
          approved_by_name: username,
          approved_by_designation: "L2 Compliance Officer",
          approved_at: new Date().toISOString(),
          approval_remarks: remarks,
        };
      } else if (currentAction === "reject") {
        updateData = {
          ...updateData,
          status: "rejected",
          rejected_by_name: username,
          rejected_by_designation: "L2 Compliance Officer",
          rejected_at: new Date().toISOString(),
          rejection_remarks: remarks,
        };
      }

      const { error } = await supabase
        .from("policy_pdfs")
        .update(updateData)
        .eq("id", currentPolicyId);

      if (error) throw error;

      await logAudit(
        currentPolicyId,
        currentAction === "approve" ? "approved" : "rejected",
        "forwarded",
        currentAction === "approve" ? "approved" : "rejected",
        remarks
      );

      setShowRemarksModal(false);
      fetchPolicies();
      alert(
        currentAction === "approve"
          ? "Policy approved successfully!"
          : "Policy rejected"
      );
    } catch (error) {
      console.error("Error updating policy:", error);
      alert("Failed to update policy");
    }
  };

  const handleGenerateFinalPDF = async (policyId) => {
    const policy = policies.find((p) => p.id === policyId);
    if (!policy) return;

    try {
      const blob = generateFinalPDF(policy, {
        name: policy.approved_by_name,
        designation: policy.approved_by_designation,
      });

      const fileName = `final_${policyId}_${Date.now()}.pdf`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("policies")
        .upload(fileName, blob, {
          contentType: "application/pdf",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("policies")
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from("policy_pdfs")
        .update({
          status: "final",
          final_url: urlData.publicUrl,
          updated_at: new Date().toISOString(),
        })
        .eq("id", policyId);

      if (updateError) throw updateError;

      await logAudit(policyId, "final_generated", "approved", "final", "");
      fetchPolicies();
      alert("Final PDF generated successfully!");
    } catch (error) {
      console.error("Error generating final PDF:", error);
      alert("Failed to generate final PDF");
    }
  };

  const handleDownload = (url, policyName) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = `${policyName.replace(/\s+/g, "_")}.pdf`;
    link.click();
  };

  const logAudit = async (policyId, action, oldStatus, newStatus, remarks) => {
    await supabase.from("policy_audit_log").insert({
      policy_pdf_id: policyId,
      action,
      performed_by: username,
      user_role: role,
      old_status: oldStatus,
      new_status: newStatus,
      remarks,
    });
  };

  const filteredPolicies = policies.filter((p) =>
    filter === "all" ? true : p.status === filter
  );

  if (loading) {
    return <div style={{ padding: 20 }}>Loading policies...</div>;
  }

  return (
    <div>
      <h3 style={{ color: "#0d47a1" }}>Policy Management ({role})</h3>

      {(role === "L1" || role === "L2") && (
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            marginBottom: "15px",
            padding: "8px",
            borderRadius: "5px",
            border: "1px solid #ccc",
         }}
  >
          {role === "L2" ? (
            <>
              <option value="forwarded">Latest / Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="final">Final</option>
            </>
          ) : (
            <>
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="draft">Draft</option>
              <option value="forwarded">Forwarded</option>
              <option value="approved">Approved</option>
              <option value="final">Final</option>
            </>
          )}    
      </select>
  )}

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          background: "white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#0d47a1", color: "white" }}>
            <th style={{ border: "1px solid #ccc", padding: "12px" }}>
              Policy Name
            </th>
            <th style={{ border: "1px solid #ccc", padding: "12px" }}>
              Status
            </th>
            <th style={{ border: "1px solid #ccc", padding: "12px" }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredPolicies.map((policy) => (
            <tr key={policy.id}>
              <td style={{ border: "1px solid #ccc", padding: "12px" }}>
                {policy.name}
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: "12px",
                  textTransform: "capitalize",
                }}
              >
                {policy.status}
              </td>
              <td style={{ border: "1px solid #ccc", padding: "12px" }}>
                {role === "L1" && policy.status === "pending" && (
                  <button
                    onClick={() => handleGenerateDraftPDF(policy.id)}
                    style={{
                      backgroundColor: "#1976d2",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "5px",
                      cursor: "pointer",
                      marginRight: "5px",
                    }}
                  >
                    Generate Draft PDF
                  </button>
                )}

                {role === "L1" && policy.status === "draft" && (
                  <>
                    <button
                      onClick={() => setSelectedPolicy(policy)}
                      style={{
                        backgroundColor: "#4caf50",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "5px",
                        cursor: "pointer",
                        marginRight: "5px",
                      }}
                    >
                      View Policy
                    </button>
                    <button
                      onClick={() => forwardToL2(policy.id)}
                      style={{
                        backgroundColor: "#ff9800",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "5px",
                        cursor: "pointer",
                      }}
                    >
                      Forward to L2
                    </button>
                  </>
                )}

                {role === "L1" &&
                  (policy.status === "forwarded" ||
                    policy.status === "approved" ||
                    policy.status === "rejected" ||
                    policy.status === "final") && (
                    <span style={{ color: "#666", fontStyle: "italic" }}>
                      Status: {policy.status}
                    </span>
                  )}

                {role === "L2" && policy.status === "forwarded" && (
                  <>
                    <button
                      onClick={() => setSelectedPolicy(policy)}
                      style={{
                        backgroundColor: "#4caf50",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "5px",
                        cursor: "pointer",
                        marginRight: "5px",
                      }}
                    >
                      View Policy
                    </button>
                    <button
                      onClick={() => openRemarksModal("approve", policy.id)}
                      style={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "5px",
                        cursor: "pointer",
                        marginRight: "5px",
                      }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => openRemarksModal("reject", policy.id)}
                      style={{
                        backgroundColor: "#f44336",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "5px",
                        cursor: "pointer",
                      }}
                    >
                      Reject
                    </button>
                  </>
                )}

                {role === "L2" && policy.status === "approved" && (
                  <button
                    onClick={() => handleGenerateFinalPDF(policy.id)}
                    style={{
                      backgroundColor: "#4caf50",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    Generate Final PDF
                  </button>
                )}

                {role === "L2" && policy.status === "final" && (
                  <>
                    <button
                      onClick={() => setSelectedPolicy(policy)}
                      style={{
                        backgroundColor: "#4caf50",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "5px",
                        cursor: "pointer",
                        marginRight: "5px",
                      }}
                    >
                      View Final Policy
                    </button>
                    <button
                      onClick={() => handleDownload(policy.final_url, policy.name)}
                      style={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "5px",
                        cursor: "pointer",
                      }}
                    >
                      Download PDF
                    </button>
                  </>
                )}

                {role === "L3" && policy.status === "final" && (
                  <>
                    <button
                      onClick={() => setSelectedPolicy(policy)}
                      style={{
                        backgroundColor: "#4caf50",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "5px",
                        cursor: "pointer",
                        marginRight: "5px",
                      }}
                    >
                      View Policy
                    </button>
                    <button
                      onClick={() => handleDownload(policy.final_url, policy.name)}
                      style={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "5px",
                        cursor: "pointer",
                      }}
                    >
                      Download PDF
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Policy Description Section */}
        <div style={{ 
          marginTop: "30px", 
          padding: "20px", 
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}>
          <h3 style={{ color: "#0d47a1", marginBottom: "15px" }}>
            Anti-money Laundering (AML) Policy
          </h3>
          <p style={{ lineHeight: 1.8, color: "#555", marginBottom: "15px" }}>
            This policy establishes our commitment to comply with UAE AML Law, FATF Recommendations and Ministry of Economy DNFBP Guidance. 
            It outlines internal procedures to prevent and detect money laundering and terrorism financing.
          </p>
          
          <div style={{ marginTop: "20px" }}>
            <p style={{ color: "#333", marginBottom: "8px" }}>
              <strong>Policy Owner:</strong> MLRO / Compliance Officer
            </p>
            <p style={{ color: "#333", marginBottom: "8px" }}>
              <strong>Version Control:</strong> Every policy version tracked, time stamped.
            </p>
            <p style={{ color: "#333", marginBottom: "8px" }}>
              <strong>Last Review Date:</strong> 01/10/2025
            </p>
            <p style={{ color: "#333", marginBottom: "8px" }}>
              <strong>Next Scheduled Review:</strong> 01/10/2026
            </p>
            <p style={{ color: "#333", marginBottom: "20px" }}>
              <strong>Approved by:</strong> Sarah Johnson, Compliance Officer, 01/10/2025
            </p>
            
            <div style={{ 
              padding: "15px", 
              backgroundColor: "#e3f2fd", 
              borderRadius: "5px",
              borderLeft: "4px solid #1976d2"
            }}>
              <p style={{ margin: 0, color: "#333" }}>
                <strong>Linked Procedures:</strong><br />
                KYC Onboarding, Sanctions Screening, STR Filing, Training
              </p>
            </div>
          </div>
        </div>
        

      {showRemarksModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "10px",
              width: "400px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            }}
          >
            <h3 style={{ marginBottom: "20px", color: "#0d47a1" }}>
              {currentAction === "approve" ? "Approval" : "Rejection"} Remarks
            </h3>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter your remarks here..."
              style={{
                width: "100%",
                height: "120px",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                fontSize: "14px",
                marginBottom: "20px",
              }}
            />
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={handleSubmitRemarks}
                style={{
                  flex: 1,
                  backgroundColor: "#1976d2",
                  color: "white",
                  border: "none",
                  padding: "10px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Submit
              </button>
              <button
                onClick={() => setShowRemarksModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: "#ccc",
                  color: "#333",
                  border: "none",
                  padding: "10px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedPolicy && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "10px",
              width: "80%",
              maxHeight: "90vh",
              overflow: "auto",
              position: "relative",
            }}
          >
            <button
              onClick={() => setSelectedPolicy(null)}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                backgroundColor: "#f44336",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "5px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              Close
            </button>
            <PolicyViewer policy={selectedPolicy} />
          </div>
        </div>
      )}
    </div>
  );
}

export default PolicyTab;